import useIndustrialTasks from "../Provider/useIndustrialTasks";
import {useMemo, useState} from "react";
import styled from "styled-components";
import {Colors} from "../Styles/Colors";
import {GiMining, GiOre, GiProcessor} from "react-icons/gi";
import {MdAccessTime, MdAddTask, MdLocationOn, MdNotes} from "react-icons/md";

const Styles = {
	Page: styled.div`
		padding: 28px;
		color: ${Colors.raw.terminal_light};
		text-align: left;
	`,
	SectionTitle: styled.div`
		font-size: 16px;
		font-weight: 800;
		margin-bottom: 12px;
	`,

	BackButton: styled.button`
		display: inline-flex;
		align-items: center;
		gap: 8px;
		width: fit-content;
		border: 1px solid rgba(255, 153, 51, 0.45);
		background: rgba(255, 122, 0, 0.10);
		color: ${Colors.raw.neon_orange};
		padding: 9px 12px;
		margin-bottom: 18px;
		cursor: pointer;
		font-family: "Courier New", monospace;
		font-size: 12px;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 1px;

		svg {
			width: 16px;
			height: 16px;
		}

		&:hover {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 14px rgba(255, 122, 0, 0.22);
		}
	`,

	Header: styled.div`
		display: grid;
		gap: 10px;
		margin-bottom: 24px;
	`,

	Eyebrow: styled.div`
		color: ${Colors.raw.neon_orange};
		letter-spacing: 3px;
		text-transform: uppercase;
		font-size: 12px;
	`,

	Title: styled.h1`
		margin: 0;
		font-size: clamp(30px, 5vw, 54px);
		line-height: 1;
		text-transform: uppercase;
		text-shadow: 0 0 18px rgba(255, 122, 0, 0.45);
	`,

	Subline: styled.div`
		max-width: 820px;
		opacity: 0.72;
		font-size: 14px;
		line-height: 1.55;
	`,

	Layout: styled.div`
		display: grid;
		grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
		gap: 18px;

		@media (max-width: 980px) {
			grid-template-columns: 1fr;
		}
	`,

	Panel: styled.section`
		position: relative;
		overflow: hidden;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background:
			radial-gradient(circle at top right, rgba(255, 122, 0, 0.18), transparent 38%),
			linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018));
		box-shadow: 0 0 20px rgba(255, 122, 0, 0.10);
		padding: 20px;
		clip-path: polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 0 100%);

		&::before {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			height: 3px;
			width: 100%;
			background: ${Colors.raw.neon_orange};
		}
	`,

	PanelTitle: styled.h2`
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 0 0 18px 0;
		color: ${Colors.raw.terminal_light};
		font-size: 20px;
		text-transform: uppercase;
		letter-spacing: 1px;

		svg {
			color: ${Colors.raw.neon_orange};
			width: 24px;
			height: 24px;
		}
	`,

	Form: styled.form`
		display: grid;
		gap: 16px;
	`,

	FieldGrid: styled.div`
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 14px;

		@media (max-width: 720px) {
			grid-template-columns: 1fr;
		}
	`,

	Field: styled.label`
		display: grid;
		gap: 7px;
	`,

	Label: styled.span`
		display: flex;
		align-items: center;
		gap: 7px;
		opacity: 0.72;
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 1.4px;

		svg {
			color: ${Colors.raw.neon_orange};
			width: 15px;
			height: 15px;
		}
	`,

	Input: styled.input`
		width: 100%;
		box-sizing: border-box;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background: rgba(0, 0, 0, 0.35);
		color: ${Colors.raw.terminal_light};
		padding: 12px 14px;
		outline: none;
		font-family: "Courier New", monospace;

		&::placeholder {
			color: rgba(255, 255, 255, 0.42);
		}

		&:focus {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
		}
	`,

	Select: styled.select`
		width: 100%;
		box-sizing: border-box;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background: rgba(0, 0, 0, 0.82);
		color: ${Colors.raw.terminal_light};
		padding: 12px 14px;
		outline: none;
		font-family: "Courier New", monospace;

		&:focus {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
		}
	`,

	Textarea: styled.textarea`
		width: 100%;
		min-height: 110px;
		box-sizing: border-box;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background: rgba(0, 0, 0, 0.35);
		color: ${Colors.raw.terminal_light};
		padding: 12px 14px;
		outline: none;
		resize: vertical;
		font-family: "Courier New", monospace;

		&::placeholder {
			color: rgba(255, 255, 255, 0.42);
		}

		&:focus {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
		}
	`,

	ActionRow: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
		margin-top: 4px;
	`,

	SubmitButton: styled.button`
		display: inline-flex;
		align-items: center;
		gap: 9px;
		border: 1px solid rgba(67, 255, 157, 0.45);
		background: rgba(67, 255, 157, 0.12);
		color: #8dffc5;
		padding: 12px 16px;
		cursor: pointer;
		font-family: "Courier New", monospace;
		font-size: 12px;
		font-weight: 900;
		text-transform: uppercase;
		letter-spacing: 1px;

		svg {
			width: 18px;
			height: 18px;
		}

		&:hover {
			box-shadow: 0 0 14px rgba(67, 255, 157, 0.22);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.42;
			box-shadow: none;
		}
	`,

	ResetButton: styled.button`
		border: 1px solid rgba(255, 153, 51, 0.34);
		background: rgba(0, 0, 0, 0.20);
		color: ${Colors.raw.neon_orange};
		padding: 12px 14px;
		cursor: pointer;
		font-family: "Courier New", monospace;
		font-size: 12px;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 1px;

		&:hover {
			border-color: ${Colors.raw.neon_orange};
		}
	`,

	Hint: styled.div`
		opacity: 0.52;
		font-size: 12px;
		line-height: 1.45;
	`,

	PreviewGrid: styled.div`
		display: grid;
		gap: 12px;
	`,

	PreviewBox: styled.div`
		border: 1px solid rgba(255, 255, 255, 0.12);
		background: rgba(0, 0, 0, 0.22);
		padding: 12px;
	`,

	PreviewLabel: styled.div`
		opacity: 0.52;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 1.4px;
		margin-bottom: 6px;
	`,

	PreviewValue: styled.div`
		color: ${Colors.raw.terminal_light};
		font-size: 14px;
		font-weight: 800;
		line-height: 1.35;
		word-break: break-word;
	`,

	JobList: styled.div`
		display: grid;
		gap: 12px;
		margin-top: 14px;
	`,

	JobCard: styled.article`
		border: 1px solid rgba(255, 153, 51, 0.25);
		background: rgba(0, 0, 0, 0.22);
		padding: 13px;
	`,

	JobTitle: styled.div`
		display: flex;
		justify-content: space-between;
		gap: 10px;
		color: ${Colors.raw.neon_orange};
		font-size: 13px;
		font-weight: 900;
		text-transform: uppercase;
		letter-spacing: 1px;
		margin-bottom: 8px;
	`,

	JobMeta: styled.div`
		opacity: 0.68;
		font-size: 12px;
		line-height: 1.45;
	`,

	EmptyState: styled.div`
		padding: 18px;
		border: 1px dashed rgba(255, 255, 255, 0.22);
		background: rgba(255, 255, 255, 0.035);
		opacity: 0.72;
		text-align: center;
		font-size: 13px;
	`,
};

const getLocationTitle = (location: {
	space_station_name: string | null;
	outpost_name: string | null;
	city_name: string | null;
	poi_name: string | null;
	terminal_name: string | null;
	orbit_name: string | null;
	planet_name: string | null;
	star_system_name: string | null;
}) =>
	location.space_station_name ||
	location.outpost_name ||
	location.city_name ||
	location.poi_name ||
	location.terminal_name ||
	location.orbit_name ||
	location.planet_name ||
	location.star_system_name ||
	"Unbekannter Standort";

const formatDateTime = (value: Date) =>
	new Date(value).toLocaleString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

export const IndustrialCreateJob = () => {

	const {locations, inProgress, Actions, materials } = useIndustrialTasks();

	const [title, setTitle] = useState("");
	const [locationId, setLocationId] = useState("");
	const [material, setMaterial] = useState<string | undefined>( undefined );
	const [amount, setAmount] = useState(0);
	const [durationInMinutes, setDurationInMinutes] = useState(30);
	const [notes, setNotes] = useState("");

	const sortedLocations = useMemo(
		() =>
			[...locations].sort((a, b) =>
				getLocationTitle(a).localeCompare(getLocationTitle(b), "de")
			),
		[locations]
	);

	const selectedLocation = useMemo(
		() => locations.find(location => String(location.id) === locationId),
		[locationId, locations]
	);

	const locationName = selectedLocation
		? getLocationTitle(selectedLocation)
		: "";

	const canSubmit =
		title.trim() !== "" &&
		locationId.trim() !== "" &&
		material &&
		amount > 0 &&
		durationInMinutes > 0;

	const resetForm = () => {
		setTitle("");
		setLocationId("");
		setMaterial( undefined );
		setAmount(0);
		setDurationInMinutes(30);
		setNotes("");
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!canSubmit) {
			return;
		}

		Actions.createJob({
			id: crypto.randomUUID(),
			location: locationName,
			material: material,
			title: title.trim(),
			note: notes.trim(),
			amount: amount,
			timestamp: new Date(),
			durationInMinutes,
		});

		resetForm();
	};
	console.log( material)

	return (
		<Styles.Page>
			<Styles.Header>
				<Styles.Eyebrow>Industrial Task Assignment</Styles.Eyebrow>
				<Styles.Title>Auftrag erstellen</Styles.Title>
				<Styles.Subline>
					Erstelle neue Industrie-Aufträge für Raffinerie-, Mining- oder
					Transportprozesse. Der Auftrag wird in der Industrie-Konsole als aktiv
					geführt.
				</Styles.Subline>
			</Styles.Header>

			<Styles.Layout>
				<Styles.Panel>
					<Styles.PanelTitle>
						<MdAddTask />
						Neuer Auftrag
					</Styles.PanelTitle>

					<Styles.Form onSubmit={handleSubmit}>
						<Styles.Field>
							<Styles.Label>
								<GiProcessor />
								Auftragstitel
							</Styles.Label>
							<Styles.Input
								value={title}
								placeholder="z.B. Quantanium raffinieren"
								onChange={event => setTitle(event.target.value)}
							/>
						</Styles.Field>

						<Styles.Field>
							<Styles.Label>
								<MdLocationOn />
								Ziel / Standort
							</Styles.Label>
							<Styles.Select
								value={locationId}
								onChange={event => setLocationId(event.target.value)}
							>
								<option value="">Standort auswählen...</option>
								{sortedLocations.map(location => (
									<option key={location.id} value={location.id}>
										{getLocationTitle(location)}
										{location.terminal_name ? ` · ${location.terminal_name}` : ""}
										{location.planet_name ? ` · ${location.planet_name}` : ""}
									</option>
								))}
							</Styles.Select>
						</Styles.Field>

						<Styles.FieldGrid>
							<Styles.Field>
								<Styles.Label>
									<GiOre />
									Material
								</Styles.Label>
								<Styles.Select
									value={material ?? ""}
									onChange={event => {
										setMaterial(event.target.value)
									}}
								>
									<option value="">Material auswählen...</option>
									{materials.map( material => (
										<option key={ material.id } value={ material.id }>
											{ material.name }
										</option>
									))}
								</Styles.Select>
							</Styles.Field>

							<Styles.Field>
								<Styles.Label>
									<GiMining />
									Menge
								</Styles.Label>
								<Styles.Input
									value={amount}
									type="number"
									placeholder="z.B. 32 SCU"
									onChange={event => setAmount(event.target.valueAsNumber)}
								/>
							</Styles.Field>
						</Styles.FieldGrid>

						<Styles.FieldGrid>
							<Styles.Field>
								<Styles.Label>
									<MdAccessTime />
									Dauer in Minuten
								</Styles.Label>
								<Styles.Input
									type="number"
									min={1}
									value={durationInMinutes}
									onChange={event =>
										setDurationInMinutes(Number(event.target.value))
									}
								/>
							</Styles.Field>
						</Styles.FieldGrid>

						<Styles.Field>
							<Styles.Label>
								<MdNotes />
								Notiz / Beschreibung
							</Styles.Label>
							<Styles.Textarea
								value={notes}
								placeholder="Zusätzliche Informationen zum Auftrag..."
								onChange={event => setNotes(event.target.value)}
							/>
						</Styles.Field>

						<Styles.ActionRow>
							<Styles.Hint>
								Pflichtfelder: Titel, Standort, Material, Size und Dauer.
							</Styles.Hint>

							<div>
								<Styles.ResetButton type="button" onClick={resetForm}>
									Zurücksetzen
								</Styles.ResetButton>{" "}
								<Styles.SubmitButton type="submit" disabled={!canSubmit}>
									<MdAddTask />
									Auftrag erstellen
								</Styles.SubmitButton>
							</div>
						</Styles.ActionRow>
					</Styles.Form>
				</Styles.Panel>

				<Styles.Panel>
					<Styles.PanelTitle>
						<GiMining />
						Auftragsvorschau
					</Styles.PanelTitle>

					<Styles.PreviewGrid>
						<Styles.PreviewBox>
							<Styles.PreviewLabel>Titel</Styles.PreviewLabel>
							<Styles.PreviewValue>
								{title.trim() || "Noch kein Titel"}
							</Styles.PreviewValue>
						</Styles.PreviewBox>

						<Styles.PreviewBox>
							<Styles.PreviewLabel>Standort</Styles.PreviewLabel>
							<Styles.PreviewValue>
								{locationName || "Kein Standort ausgewählt"}
							</Styles.PreviewValue>
						</Styles.PreviewBox>

						<Styles.PreviewBox>
							<Styles.PreviewLabel>Material / Size / Dauer</Styles.PreviewLabel>
							<Styles.PreviewValue>
								{materials.find( a => a.id === material)?.name } · {amount || "---"} SCU · {durationInMinutes || 0} Minuten
							</Styles.PreviewValue>
						</Styles.PreviewBox>
					</Styles.PreviewGrid>

					<Styles.SectionTitle>Aktive Aufträge</Styles.SectionTitle>

					{inProgress.length === 0 ? (
						<Styles.EmptyState>
							Noch keine aktiven Industrie-Aufträge vorhanden.
						</Styles.EmptyState>
					) : (
						<Styles.JobList>
							{inProgress.map(job => (
								<Styles.JobCard key={job.id}>
									<Styles.JobTitle>
										<span>{job.material}</span>
										<span>{job.durationInMinutes} min</span>
									</Styles.JobTitle>

									<Styles.JobMeta>
										Standort: {job.location}
										<br />
										Menge: {job.amount}
										<br />
										Erstellt: {formatDateTime(job.timestamp)}
									</Styles.JobMeta>
								</Styles.JobCard>
							))}
						</Styles.JobList>
					)}
				</Styles.Panel>
			</Styles.Layout>
		</Styles.Page>
	);
};